import React, { useState, useRef, useCallback, useEffect } from 'react';
import MentionAutocomplete from './MentionAutocomplete';
import compressImage from '../../lib/image_compressor';

// 入力欄の最大表示行数。これを超えたら枠は広げず内部スクロールにする（Slack 準拠）。
const MAX_ROWS = 10;

// @context TGW-1853 入力欄の高さを内容に合わせて MAX_ROWS 行まで自動拡張し、超過分はスクロール。
const autoResize = (el) => {
  if (!el) return;
  el.style.height = 'auto';
  const cs = window.getComputedStyle(el);
  const lineHeight = parseFloat(cs.lineHeight) || 20;
  const paddingV =
    (parseFloat(cs.paddingTop) || 0) + (parseFloat(cs.paddingBottom) || 0);
  const borderV =
    (parseFloat(cs.borderTopWidth) || 0) +
    (parseFloat(cs.borderBottomWidth) || 0);
  const maxHeight = lineHeight * MAX_ROWS + paddingV + borderV;
  el.style.height = `${Math.min(el.scrollHeight, maxHeight)}px`;
  el.style.overflowY = el.scrollHeight > maxHeight ? 'auto' : 'hidden';
};

// @context TGW-1769 入力欄。画像1枚（BE制約）。
//   @context TGW-1853 送信キー（Slack準拠・Enter単体は改行＝誤送信防止）:
//     Shift+Enter / Cmd+Enter(Mac) / Ctrl+Enter(Win) で送信、Enter単体は改行。
//     Enter改行で MAX_ROWS 行まで枠が自動拡張し、超過分は内部スクロール。
//   @補完: 選択時に安定キー「@tunagate_id」を挿入し user_id を蓄積 → 送信時に mention_user_ids[] で渡す。
//   本文にあだ名（users_display_names＝送信者個人の別名）を焼き込むと受信者に漏れる & 本人確認非公開
//   ユーザーは名前が「@tunagate_id」形になり @@ 二重付与になるため、あだ名ではなく tunagate_id を埋める。
//   表示側（decorateMentions）が @tunagate_id を各閲覧者文脈の名前に差し替える。
//   本文パースでの id 逆引きはしない（同姓同名・改名で壊れるため）。id は選択時に明示蓄積する。
export default function MessageComposer({
  circleId,
  channel,
  onSend,
  placeholder,
}) {
  const [body, setBody] = useState('');
  const [file, setFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [mentionUsers, setMentionUsers] = useState([]); // {id, label}
  const [mentionChannel, setMentionChannel] = useState(false);
  const [mentionEveryone, setMentionEveryone] = useState(false);
  const [mentionQuery, setMentionQuery] = useState(null); // null=補完閉、''以上=検索中
  const [candidates, setCandidates] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const debounceRef = useRef(null);

  // 集合メンション候補: @channel は常時、@everyone は全体チャンネルのみ（要件15）
  const groupOptions = [];
  if (mentionQuery !== null) {
    const q = mentionQuery.toLowerCase();
    if (!channel.isGeneral && 'channel'.startsWith(q)) {
      groupOptions.push({
        key: 'channel',
        isGroup: true,
        label: '@channel',
        description: 'チャンネルのメンバー全員に通知',
      });
    }
    if (channel.isGeneral && 'everyone'.startsWith(q)) {
      groupOptions.push({
        key: 'everyone',
        isGroup: true,
        label: '@everyone',
        description: 'サークル全員に通知',
      });
    }
  }

  // @入力の検出: caret 直前の「@クエリ」を拾う
  const detectMention = useCallback((value, caret) => {
    const before = value.slice(0, caret);
    const match = before.match(/(^|[\s\n])@([^\s@]*)$/);
    return match ? match[2] : null;
  }, []);

  useEffect(() => {
    if (mentionQuery === null) {
      setCandidates([]);
      return undefined;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await fetch(
          `/api/message/mention_candidates?circle_id=${circleId}&q=${encodeURIComponent(mentionQuery)}`,
          {
            credentials: 'same-origin',
            headers: { Accept: 'application/json' },
          },
        );
        if (res.ok) {
          const data = await res.json();
          setCandidates(data.candidates || []);
          setActiveIndex(0);
        }
      } catch (e) {
        setCandidates([]);
      }
    }, 200);
    return () => clearTimeout(debounceRef.current);
  }, [mentionQuery, circleId]);

  // body の変化（入力・メンション挿入・送信後クリア）に追従して高さを再計算する。
  useEffect(() => {
    autoResize(textareaRef.current);
  }, [body]);

  const handleChange = (e) => {
    const { value, selectionStart } = e.target;
    setBody(value);
    setMentionQuery(detectMention(value, selectionStart));
    autoResize(e.target);
  };

  const insertMention = (option) => {
    const el = textareaRef.current;
    const caret = el ? el.selectionStart : body.length;
    const before = body.slice(0, caret).replace(/@([^\s@]*)$/, '');
    const after = body.slice(caret);

    if (option.isGroup) {
      const token = `@${option.key} `;
      setBody(`${before}${token}${after}`);
      if (option.key === 'channel') setMentionChannel(true);
      if (option.key === 'everyone') setMentionEveryone(true);
    } else {
      // @context TGW-1853 安定キー @tunagate_id を挿入（あだ名は焼き込まない）。tunagate_id が無い
      //   例外時のみ表示名にフォールバックし、その際も先頭の @ を除去して @@ 二重を防ぐ。
      const handle = String(option.tunagate_id || '').trim();
      const label = handle || String(option.name || '').replace(/\s+/g, '').replace(/^@+/, '');
      const token = `@${label} `;
      setBody(`${before}${token}${after}`);
      setMentionUsers((prev) =>
        prev.some((u) => u.id === option.id)
          ? prev
          : [...prev, { id: option.id }],
      );
    }
    setMentionQuery(null);
    if (el) el.focus();
  };

  const popupOpen =
    mentionQuery !== null && groupOptions.length + candidates.length > 0;

  const handleKeyDown = (e) => {
    if (popupOpen) {
      const total = groupOptions.length + candidates.length;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex((i) => (i + 1) % total);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex((i) => (i - 1 + total) % total);
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        const all = [...groupOptions, ...candidates];
        insertMention(all[activeIndex]);
        return;
      }
      if (e.key === 'Escape') {
        setMentionQuery(null);
        return;
      }
    }
    // @context TGW-1853 送信キー（Slack準拠）: Enter単体は改行（誤送信防止）。
    //   Shift+Enter / Cmd+Enter(Mac) / Ctrl+Enter(Win) で送信。IME変換確定中は送信しない。
    if (
      e.key === 'Enter' &&
      (e.shiftKey || e.metaKey || e.ctrlKey) &&
      !e.nativeEvent.isComposing
    ) {
      e.preventDefault();
      submit();
    }
  };

  // @context TGW-1769 Phase2 DM側（message_send_form）と同じ送信前圧縮を適用。
  //   HEIC→JPEG変換と5MB超の圧縮をしないと、BEのサイズ/形式バリデーションで送信失敗する。
  const handleFileChange = async (e) => {
    const picked = e.target.files && e.target.files[0];
    if (!picked) return;
    const compressed = await compressImage(picked);
    setFile(compressed);
    setFilePreview(URL.createObjectURL(compressed));
  };

  const clearFile = () => {
    setFile(null);
    if (filePreview) URL.revokeObjectURL(filePreview);
    setFilePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const submit = () => {
    const trimmed = body.trim();
    if (!trimmed && !file) return;
    onSend({
      body: trimmed,
      file,
      mentionUserIds: mentionUsers.map((u) => u.id),
      mentionChannel,
      mentionEveryone,
    });
    setBody('');
    clearFile();
    setMentionUsers([]);
    setMentionChannel(false);
    setMentionEveryone(false);
    setMentionQuery(null);
  };

  return (
    <div className="slackch-composer">
      {popupOpen && (
        <MentionAutocomplete
          groupOptions={groupOptions}
          candidates={candidates}
          activeIndex={activeIndex}
          onSelect={insertMention}
        />
      )}
      <div className="slackch-composer-box">
        {filePreview && (
          <div className="slackch-composer-preview">
            <img src={filePreview} alt="添付画像" />
            <button type="button" onClick={clearFile} aria-label="添付を削除">
              ✕
            </button>
          </div>
        )}
        <textarea
          ref={textareaRef}
          className="slackch-composer-textarea"
          value={body}
          placeholder={placeholder}
          rows={2}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
        />
        <div className="slackch-composer-actions">
          <button
            type="button"
            className="slackch-composer-icon"
            title="メンション"
            onClick={() => {
              setBody((v) => `${v}@`);
              setMentionQuery('');
              if (textareaRef.current) textareaRef.current.focus();
            }}
          >
            ＠
          </button>
          <label className="slackch-composer-icon" title="画像を添付">
            🖼️
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              hidden
            />
          </label>
          <button
            type="button"
            className="slackch-composer-send"
            onClick={submit}
            disabled={!body.trim() && !file}
          >
            送信
          </button>
        </div>
      </div>
    </div>
  );
}
